import React, { useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import moment from "moment";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import getTooltipTitle from "../../utils/getTooltip";
import "chartjs-adapter-moment";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
);

const exportToCSV = (dataObj) => {
  const { datasets, labels } = dataObj;
  const indices = Object.keys(datasets);

  let csvHeader = ["Timestamp"];
  indices.forEach((idx) => {
    csvHeader.push(datasets[idx].label);
  });
  let csvContent = csvHeader.join(",") + "\n";

  for (let i = 0; i < labels.length; i++) {
    let row = [labels[i]];
    indices.forEach((idx) => {
      row.push(datasets[idx].data[i]);
    });
    csvContent += row.join(",") + "\n";
  }

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "data.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function HistoryChart({ chartData }) {
  // 处理同一分钟内的数据合并

  const startTimestamp = moment(chartData.list?.[0].datetime);
  const endTimestamp = moment(chartData.list?.[chartData.list.length - 1].datetime);
  const { t } = useTranslation();
  const data = { labels: [], datasets: [] };
  const lineColors = [
    "#011128",
    "#002D5B",
    "#00B6DE",
    "#0076A8",
    "#73DFF5",
    "#78BE20",
    "#CBDB2F",
    "#FEB81C",
    "#FFD500",
    "#F58200",
  ];
  for (var property in chartData.list[0].values) {
    var dataset = {
      label: getTooltipTitle(property, t),
      data: chartData.list.map((item) => item.values[property]),
      fill: false,
      borderColor: lineColors.pop(),
      tension: 0.1,
    };
    data.datasets.push(dataset);
  }

  data.labels = chartData.list.map((item) => moment(item.datetime));
  const handleExport = () => {
    exportToCSV(data);
  };
  // 计算时间跨度（以小时为单位）
  const timeSpanInHours = endTimestamp.diff(startTimestamp, "hours");

  // 根据时间跨度动态调整刻度间隔
  let tickInterval;
  if (timeSpanInHours <= 24) {
    tickInterval = 1;
  } else if (timeSpanInHours <= 72) {
    tickInterval = 3;
  } else if (timeSpanInHours <= 168) {
    tickInterval = 6;
  } else {
    tickInterval = 12;
  }

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        width: "100%",
        minHeight: "480px",
      }}
    >
      <Button
        onClick={handleExport}
        style={{
          position: "absolute",
          left: "10px",
          top: "10px",
        }}
      >
        {t("history.chart_exporttocsv")}
      </Button>
      <Line
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          stacked: false,
          plugins: {
            title: {
              display: true,
              text: t("history.chart_historydata"),
            },
          },
          scales: {
            x: {
              type: "time",
              time: {
                unit: "hour",
                displayFormats: {
                  hour: "HH:mm",
                },
                tooltipFormat: "DD/MM/YYYY HH:mm",
              },
              ticks: {
                stepSize: tickInterval,
              },
              display: true,
            },
            x2: {
              type: "time",
              position: "bottom",
              time: {
                unit: "day",
                displayFormats: {
                  day: "DD/MM/YYYY",
                },
              },
              grid: {
                drawOnChartArea: false,
              },
              title: {
                display: true,
                text: t("history.chart_date"),
              },
            },
            y: {
              display: true,
              title: {
                display: true,
                text: t("history.chart_values"),
              },
            },
          },
        }}
        data={data}
      />
    </div>
  );
}
